import { Button } from "@mui/material";
import { FaRegEdit } from "react-icons/fa";
import { Col, List, Row, Skeleton, Tooltip, Typography } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { AiOutlineDelete, AiOutlineInfoCircle } from "react-icons/ai";
import {
  LoadingOutlined,
  PlusCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Container } from "./styles";
import { formatCurrency } from "../../../../utils";
import AntButton from "../../../../components/Button";
import { FormModal } from "../../../../components/FormModal";
import { AddTransactionGOVFormItemRows } from "../../../../constants/darfBolsa";

export interface OldTransactionsProps {
  changed: boolean;
  loadingPut: boolean;
  loadingGet: boolean;
  setLoadingPut: Dispatch<SetStateAction<boolean>>;
  setLoadingGet: Dispatch<SetStateAction<boolean>>;
  oldPositionEdit: any;
  oldPositionList: any[];
  showAddPositionModal: boolean;
  downloadOldTransactionsExcel: () => Promise<null | Blob>;
  setOldPositionEdit: Dispatch<SetStateAction<any>>;
  setShowAddPositionModal: Dispatch<SetStateAction<boolean>>;
  saveOldPositions: () => void;
  addOldPosition: (item: any) => void;
  editOldPosition: (item: any) => void;
  handleEditOldPosition: (item: any, index: number) => void;
  handleRemoveOldPosition: (item: any, index: number) => void;
  getOldTransactionsInit: () => void;
}

export const OldTransactions: React.FC<OldTransactionsProps> = ({
  loadingGet,
  loadingPut,
  oldPositionList,
  downloadOldTransactionsExcel,
  setOldPositionEdit,
  setShowAddPositionModal,
  handleEditOldPosition,
  handleRemoveOldPosition,
  addOldPosition,
  editOldPosition,
  showAddPositionModal,
  oldPositionEdit,
}) => {
  const [hasLendingPosition, setHasLendingPosition] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (oldPositionEdit?._id || oldPositionEdit?.id) {
      if (oldPositionEdit?.lendingSide) {
        setHasLendingPosition(true);
      } else {
        setHasLendingPosition(false);
      }
    }
  }, [oldPositionEdit]);

  async function handleDownloadOldTransactions() {
    setDownloading(true);
    const file = await downloadOldTransactionsExcel();
    if (file) {
      const blob = new Blob([file], { type: "xlsx" });
      const element = document.createElement("a");
      element.href = URL.createObjectURL(blob);
      element.download = "carteira_31-12-19.xlsx";
      document.body.appendChild(element);
      element.click();
      element.remove();
    }
    setDownloading(false);
  }

  return (
    <Container>
      <Row justify="end" align="top" gutter={[16, 16]}>
        <Col flex={1} offset={0}>
          <Typography.Title style={{ justifySelf: "center", marginLeft: 'auto' }} level={2}>
            Ativos em carteira
          </Typography.Title>
        </Col>
        <Col flex={1}>
          <Row justify="space-between" align="middle">
            {oldPositionList.length === 0 ? (
              <div />
            ) : (
              <Button
                size="small"
                color="secondary"
                variant="contained"
                disabled={oldPositionList.length === 0}
                startIcon={
                  loadingPut || loadingGet || downloading ? (
                    <LoadingOutlined style={{ color: "white" }} />
                  ) : (
                    <DownloadOutlined style={{ color: "white" }} />
                  )
                }
                onClick={handleDownloadOldTransactions}>
                BAIXAR EXCEL
              </Button>
            )}
            <Button
              size="small"
              color="secondary"
              variant="contained"
              onClick={() => {
                setOldPositionEdit({});
                setHasLendingPosition(false);
                setShowAddPositionModal(true);
              }}
              startIcon={
                loadingPut || loadingGet ? (
                  <LoadingOutlined style={{ color: "white" }} />
                ) : (
                  <PlusCircleOutlined style={{ color: "white" }} />
                )
              }>
              Adicionar Posição
            </Button>
          </Row>
        </Col>
        <Col span={24}>
          <List
            loading={loadingGet}
            itemLayout="horizontal"
            dataSource={
              oldPositionList?.filter(
                (oldPosition) => !oldPosition.lendingSide
              ) ?? []
            }
            renderItem={(item, index) => (
              <List.Item
                className={item.price === 0 ? "error-border" : ""}
                actions={[
                  ...(item.price === 0
                    ? [
                        <Tooltip title="Preencha o custo médio!">
                          <AntButton
                            icon={
                              <AiOutlineInfoCircle
                                className="alert-icon"
                                size={20}
                              />
                            }
                            type="text"
                          />
                        </Tooltip>,
                      ]
                    : []),
                  <AntButton
                    type="text"
                    disabled={loadingPut}
                    icon={<FaRegEdit />}
                    onClick={() => handleEditOldPosition(item, index)}
                  />,
                  <AntButton
                    type="text"
                    disabled={loadingPut}
                    icon={<AiOutlineDelete />}
                    onClick={() => handleRemoveOldPosition(item, index)}
                  />,
                ]}>
                <Skeleton active title={false} loading={loadingGet}>
                  <List.Item.Meta
                    title={<div>{item.code}</div>}
                    description={
                      <>
                        <div className="list-description">
                          <div>
                            Quantidade: <strong>{item.quantity}</strong>
                          </div>
                          <div>
                            Custo médio:{" "}
                            <strong>{formatCurrency(item.price ?? 0)}</strong>
                          </div>
                        </div>
                      </>
                    }
                  />
                </Skeleton>
              </List.Item>
            )}
          />
        </Col>
      </Row>
      <Row className="row-margin" justify="end" align="top" gutter={[16, 16]}>
        <Col flex={1} offset={0}>
          <Typography.Title level={2}>Empréstimo de ativos</Typography.Title>
        </Col>
        <Col>
          <Button
            size="small"
            color="secondary"
            variant="contained"
            startIcon={
              loadingPut || loadingGet ? (
                <LoadingOutlined style={{ color: "white" }} />
              ) : (
                <PlusCircleOutlined style={{ color: "white" }} />
              )
            }
            onClick={() => {
              setOldPositionEdit({});
              setHasLendingPosition(true);
              setShowAddPositionModal(true);
            }}>
            Adicionar Posição
          </Button>
        </Col>
        <Col span={24}>
          <List
            loading={loadingGet}
            itemLayout="horizontal"
            dataSource={
              oldPositionList?.filter(
                (oldPosition) => oldPosition.lendingSide
              ) ?? []
            }
            renderItem={(item, index) => (
              <List.Item
                className={item.price === 0 ? "error-border" : ""}
                actions={[
                  ...(item.price === 0
                    ? [
                        <Tooltip title="Preencha o custo médio!">
                          <AntButton
                            icon={
                              <AiOutlineInfoCircle
                                className="alert-icon"
                                size={20}
                              />
                            }
                            type="text"
                          />
                        </Tooltip>,
                      ]
                    : []),
                  <AntButton
                    type="text"
                    disabled={loadingPut}
                    icon={<FaRegEdit />}
                    onClick={() => handleEditOldPosition(item, index)}
                  />,
                  <AntButton
                    type="text"
                    disabled={loadingPut}
                    icon={<AiOutlineDelete />}
                    onClick={() => handleRemoveOldPosition(item, index)}
                  />,
                ]}>
                <Skeleton active title={false} loading={loadingGet}>
                  <List.Item.Meta
                    title={<div>{item.code}</div>}
                    description={
                      <>
                        <div className="list-description">
                          <div>
                            Quantidade: <strong>{item.quantity}</strong>
                          </div>
                          <div>
                            Custo médio:{" "}
                            <strong>{formatCurrency(item.price ?? 0)}</strong>
                          </div>
                        </div>
                      </>
                    }
                  />
                </Skeleton>
              </List.Item>
            )}
          />
        </Col>
      </Row>

      <FormModal
        add={addOldPosition}
        edit={editOldPosition}
        visibility={showAddPositionModal}
        rows={AddTransactionGOVFormItemRows(hasLendingPosition)}
        onCancel={() => {
          setShowAddPositionModal(false);
          setHasLendingPosition(false);
        }}
        title={
          oldPositionEdit?._id || oldPositionEdit?.id
            ? "Editar ativo"
            : "Adicionar ativo"
        }
        itemToEdit={
          oldPositionEdit.id || oldPositionEdit._id
            ? oldPositionEdit
            : undefined
        }
      />
    </Container>
  );
};
